import React, { useMemo } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, getStraightPath, useReactFlow, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { getBusLayout, getBusHandleAnchor } from '../../../../utilities/busLayout';

// Build attribute UI similar to CustomNode.js: show visible attributes in rows
const buildEdgeAttributeRows = (edge, separatorText = ':') => {
  const attrs = edge?.data?.attributes;
  if (!attrs) return null;

  const rows = Object.entries(attrs)
    .filter(([_, attr]) => {
      if (!attr || !attr.isVisible) return false;
      const raw = attr.value;
      if (raw === undefined || raw === null) return false;
      const text = typeof raw === 'string' ? raw.trim() : String(raw).trim();
      return text.length > 0;
    })
    .map(([key, attr]) => {
      const label = attr.label ?? key;
      const displayValue = typeof attr.value === 'string' ? attr.value.trim() : attr.value;
      const safeSeparator = typeof separatorText === 'string' ? separatorText : ':';
      const showSeparator = safeSeparator.length > 0;
      const labelWithSeparator = showSeparator ? `${label}${safeSeparator}` : label;
      return (
        <div key={key} className="attr-row">
          <div className="attr-row__label bold">{labelWithSeparator}</div>
          <div className="attr-row__value">{displayValue}</div>
        </div>
      );
    });

  return rows.length > 0 ? rows : null;
};

// No legacy text: if no structured visible attributes, render nothing

const CableEdge = (props) => {
  const { id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, selected } = props;
  
  const scaleCm = useSelector((state) => state.view.scaleCm);
  // Subscribe to source/target node angle so edge re-renders on rotation
  const srcAngle = useSelector((state) => {
    const n = (state.node.nodes || []).find((x) => x.id === source);
    return typeof n?.data?.angle === 'number' ? n.data.angle : 0;
  });
  const tgtAngle = useSelector((state) => {
    const n = (state.node.nodes || []).find((x) => x.id === target);
    return typeof n?.data?.angle === 'number' ? n.data.angle : 0;
  });
  const reactFlow = useReactFlow();
  const gridGapPx = useSelector((state) => state.view.gridGapPx) || 25;
  const PX_PER_CM = 37.7952755906;
  const BASE_PX = 100;
  const scaleFactor = Math.max(0.1, Math.min(5, ((scaleCm || (BASE_PX / PX_PER_CM)) * PX_PER_CM) / BASE_PX));

  // If either node is rotated, override anchors to side centers based on angle
  const getNodeById = (nid) => (reactFlow?.getNodes?.() || []).find((n) => n.id === nid);
  const busLayoutCache = new Map();
  const getLayoutForNode = (node) => {
    if (!node || node.type !== 'busNode') return null;
    if (busLayoutCache.has(node.id)) return busLayoutCache.get(node.id);
    const layout = getBusLayout(node, gridGapPx);
    busLayoutCache.set(node.id, layout);
    return layout;
  };

  const calcAnchor = (node, role, handleId) => {
    if (!node) return null;
    const x = (node.positionAbsolute?.x ?? node.position?.x ?? 0);
    const y = (node.positionAbsolute?.y ?? node.position?.y ?? 0);
    const w = node.width || 0;
    const h = node.height || 0;
    const ang = typeof node?.data?.angle === 'number' ? node.data.angle : 0;
    const isRot = !!node?.data?.isRotable;
    // Bus node: compute per-handle anchors
    if (node?.type === 'busNode') {
      const layout = getLayoutForNode(node);
      if (layout) {
        const roleKey = role === 'source' ? 'source' : 'target';
        return getBusHandleAnchor(node, layout, roleKey, handleId);
      }
    }
    // For non-bus nodes without explicit handles, snap to side centers based on angle
    const listMaybe = Array.isArray(node?.data?.handles) ? node.data.handles : null;
    if (!listMaybe || listMaybe.length === 0) {
      const vertical = ang === 0;
      if (vertical) {
        return role === 'source' ? { x: x + w / 2, y: y + h } : { x: x + w / 2, y };
      }
      return role === 'source' ? { x: x + w, y: y + h / 2 } : { x, y: y + h / 2 };
    }
    // For non-bus nodes with explicit handles, compute anchor for the exact handle id
    try {
      const list = Array.isArray(node?.data?.handles) ? node.data.handles : null;
      if (!list || list.length === 0) return null;
      if (!handleId && list.filter(h => (h?.type || '').toLowerCase() === (role || '').toLowerCase()).length !== 1) return null;
      const hc = list.find((h) => String(h?.id) === String(handleId)) || list.find((h) => (h?.type || '').toLowerCase() === (role || '').toLowerCase());
      if (!hc) return null;
      const mapPos = (pos) => {
        if (ang === 0) return pos;
        switch (pos) {
          case Position.Top: return Position.Left;
          case Position.Bottom: return Position.Right;
          case Position.Left: return Position.Top;
          case Position.Right: return Position.Bottom;
          default: return pos;
        }
      };
      const mapped = mapPos(hc.position);
      const style = hc?.style || {};
      const pickPercent = (val, total) => {
        if (typeof val === 'number') return Math.max(0, Math.min(1, val / total));
        const s = String(val || '').trim();
        if (s.endsWith('%')) {
          const p = parseFloat(s.slice(0, -1));
          if (!isNaN(p)) return Math.max(0, Math.min(1, p / 100));
        }
        return 0.5;
      };
      const frac = (hc.position === Position.Top || hc.position === Position.Bottom)
        ? pickPercent(style.left, w)
        : pickPercent(style.top, h);
      switch (mapped) {
        case Position.Top: return { x: x + w * frac, y };
        case Position.Bottom: return { x: x + w * frac, y: y + h };
        case Position.Left: return { x, y: y + h * frac };
        case Position.Right: return { x: x + w, y: y + h * frac };
        default: return null;
      }
    } catch {}
    // Fallback to RF props
    return null;
  };

  const sN = getNodeById(source);
  const tN = getNodeById(target);
  // Read handle ids from RF edge state so we respect the exact handle used
  const rfEdge = (reactFlow?.getEdges?.() || []).find((e) => e.id === id);
  const sOv = calcAnchor(sN, 'source', rfEdge?.sourceHandle);
  const tOv = calcAnchor(tN, 'target', rfEdge?.targetHandle);
  const SX = sOv?.x ?? sourceX;
  const SY = sOv?.y ?? sourceY;
  const TX = tOv?.x ?? targetX;
  const TY = tOv?.y ?? targetY;

  const [edgePath, labelX, labelY] = useMemo(() => {
    const dx = TX - SX;
    const dy = TY - SY;
    const distance = Math.hypot(dx, dy);
    const threshold = (data && data.options && data.options.straightThreshold) || 80; 

    if (distance <= threshold) {
      return getStraightPath({ sourceX: SX, sourceY: SY, targetX: TX, targetY: TY });
    }

    return getSmoothStepPath({ sourceX: SX, sourceY: SY, sourcePosition, targetX: TX, targetY: TY, targetPosition, borderRadius: 0 });
  }, [SX, SY, TX, TY, sourcePosition, targetPosition, data, srcAngle, tgtAngle]);

  // Offset label a bit so it's beside the line rather than on top
  const offsetX = 12;
  const offsetY = -12;
  const attributeLabelSeparator = useSelector((state) => state.view.attributeLabelSeparator ?? ':');
  const attributeRows = buildEdgeAttributeRows({ data }, attributeLabelSeparator);
  const labelFontPx = useSelector((state) => state.view.labelFontPx) || 38;
  const selectedEdgeIds = useSelector((state) => state.edge.selectedEdgeIds) || [];
  const isSelected = selectedEdgeIds.includes(id) || selected;

  const lineStrokePx = useSelector((state) => state.view.lineStrokePx) || 3;
  const strokeWidth = Math.max(1, lineStrokePx);
  const baseColor = (data && data.options && typeof data.options.stroke === 'string')
    ? data.options.stroke.trim()
    : null;
  const resolvedColor = baseColor || '#222';
  const resizingBusNodeId = useSelector((state) => state.view.resizingBusNodeId);
  const isBusResizeConnection = !!resizingBusNodeId && (source === resizingBusNodeId || target === resizingBusNodeId);
  const strokeColor = isBusResizeConnection ? '#9ca3af' : (isSelected ? (baseColor || '#2cabef') : resolvedColor);

  return (
    <>
      <BaseEdge id={id} path={edgePath} style={{ strokeWidth, stroke: strokeColor }} />
      {attributeRows && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(0%, -50%) translate(${labelX + offsetX}px, ${labelY + offsetY}px)`,
              pointerEvents: 'none',
            }}
            className="nodrag nopan"
          >
            <div
              style={{
                background: '#ffffff00',
                border: '0px solid #e0e0e0',
                borderRadius: 4,
                padding: '4px 8px',
                color: '#333',
                fontSize: labelFontPx,
                lineHeight: `${labelFontPx}px`
              }}
              className="table-container"
            >
              {attributeRows}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default CableEdge;
